import { matchesNew } from '@components/bid_request/details/utils/matchesNew';
import type {
  CustomColumnItem,
  ID,
  LineItem,
  LineItemChanges,
  LotChanges,
} from 'types';

const ROW_BOOKKEEPING_KEYS = new Set(['id', 'lot_id']);

const isBlank = (text?: string | null): boolean => text == null || text === '';

const savedCell = (originalItem: LineItem | undefined, headerId: ID): CustomColumnItem | undefined => (
  originalItem?.custom_column_items?.find(item => String(item.custom_column_header_id) === String(headerId))
);

/**
 * A column-walk cell: its header is new or changed in the lot's pending headers, its value is blank
 * or the saved value, and its visibility is the header's pending value, or the saved value when the
 * header has none pending. Any other cell under that column is an edit.
 */
const isWalkCell = (
  cell: CustomColumnItem,
  lotChanges: LotChanges,
  originalItem: LineItem | undefined,
): boolean => {
  const header = lotChanges.custom_column_headers?.[cell.custom_column_header_id];
  if (cell._destroy || !header) return false;

  const saved = savedCell(originalItem, cell.custom_column_header_id);
  const isWalkValue = isBlank(cell.display_text) || cell.display_text === saved?.display_text;
  const isWalkVisibility = cell.is_supplier_visible === undefined
    || cell.is_supplier_visible === (header.is_supplier_visible ?? saved?.is_supplier_visible);

  return isWalkValue && isWalkVisibility;
};

const hasEdit = (
  rowChanges: LineItemChanges,
  lotChanges: LotChanges,
  originalItem: LineItem,
): boolean => {
  const editedField = Object.keys(rowChanges)
    .some(key => !ROW_BOOKKEEPING_KEYS.has(key) && key !== 'custom_column_items');
  if (editedField) return true;

  return Object.values(rowChanges.custom_column_items ?? {})
    .some(cell => Boolean(cell) && !isWalkCell(cell, lotChanges, originalItem));
};

export interface IsRowSentParams {
  lineItemId: ID;
  lotChanges?: LotChanges;
  originalItemsById: Map<string, LineItem>;
}

/**
 * The one predicate for "a row the save sends", shared by the Save gate and the paging lock: a new
 * row, a row marked for deletion, or a saved row with a pending change other than column-walk cells.
 * A saved row that is not held is walk-only: any other edit locks paging, so only a column walk on a
 * page the buyer has since left writes its entry.
 */
export const isRowSent = ({ lineItemId, lotChanges, originalItemsById }: IsRowSentParams): boolean => {
  if (!lotChanges || lotChanges._destroy) return false;

  const rowChanges = lotChanges.line_items?.[lineItemId];
  if (!rowChanges) return false;
  if (matchesNew(lineItemId)) return true;

  const originalItem = originalItemsById.get(String(lineItemId));
  if (!originalItem) return false;
  if (rowChanges._destroy) return true;

  return hasEdit(rowChanges, lotChanges, originalItem);
};
